import { inject, injectable, injectFromBase } from 'inversify'
import ExternalTrackerService from '@/services/tracking/externalTrackerService'
// eslint-disable-next-line tp/forbid-import-composable-to-service,tp/using-vue-in-services-restriction
import type { Plugin } from '@/composition/vue/compositionApi'
import type { ExternalTrackerAsPlugin, TrackableConfig } from '@/services/tracking/types'
import { SERVICE_TYPES } from '@/core/container/types'
import type CookieConsentService from '@/services/browser/cookieConsentService'
import type SubscriptionService from '@/services/transport/subscriptionService'
import type UserService from '@/services/domain/user/userService'
import { ROUTER_AFTER_EACH } from '@/services/route/types'

@injectable()
@injectFromBase()
export class TikTokPixelService extends ExternalTrackerService {
  protected readonly trackingId = 'DB7N68RC77UB6E0E8OV0'

  protected isSdkRequested = false

  protected appliedConsent: boolean | undefined = undefined

  protected identifiedUserId: string | undefined = undefined

  constructor(
    @inject(SERVICE_TYPES.CookieConsentService) protected readonly cookieConsentService: CookieConsentService,
    @inject(SERVICE_TYPES.SubscriptionService) protected readonly subscriptionService: SubscriptionService,
    @inject(SERVICE_TYPES.UserService) protected readonly userService: UserService,
  ) {
    super()
  }

  public getConfig(): TrackableConfig {
    return {
      id: this.trackingId,
    }
  }

  public getPlugin(): Plugin {
    return {
      install: () => this.initialize(),
    } satisfies Plugin
  }

  protected async initialize(): Promise<void> {
    const subscriptionKey = 'tikTokPixel'

    this.syncConsent()

    if (this.subscriptionService.exist(subscriptionKey)) {
      return
    }

    this.subscriptionService.subscribeWithSubscriptionKey(
      ROUTER_AFTER_EACH,
      () => {
        this.syncConsent()
        this.trackPageView()
      },
      subscriptionKey,
      true,
    )
    document.addEventListener('visibilitychange', this.visibilityHandler)
  }

  public override cleanUp(): void {
    document.removeEventListener('visibilitychange', this.visibilityHandler)
  }

  protected visibilityHandler = () => {
    if (!document.hidden) {
      this.syncConsent()
    }
  }

  protected syncConsent(): void {
    const hasConsent = this.cookieConsentService.hasAdvertisementConsent()

    if (hasConsent === this.appliedConsent) {
      return
    }

    this.appliedConsent = hasConsent

    if (!this.isSdkRequested) {
      if (hasConsent) {
        this.loadSdk()
      }
      return
    }

    if (hasConsent) {
      window.ttq?.grantConsent()
    } else {
      window.ttq?.revokeConsent()
    }
  }

  protected trackPageView(): void {
    if (this.isSdkRequested) {
      window.ttq?.page()
    }
  }

  protected identifyUser(): void {
    const userId = this.userService.getCurrentUserOrNull()?.id

    if (!userId || userId === this.identifiedUserId) {
      return
    }

    this.identifiedUserId = userId
    // The SDK hashes identifiers itself before sending
    window.ttq?.identify({ external_id: userId })
  }

  protected loadSdk(): void {
    this.isSdkRequested = true

    /* eslint-disable */
    /* prettier-ignore */
    const trackingHandler = async () => {
      // @ts-expect-error tracker initial script from docs
      !function(w,d,t){w.TiktokAnalyticsObject=t;var ttq=w[t]=w[t]||[];ttq.methods=["page","track","identify","instances","debug","on","off","once","ready","alias","group","enableCookie","disableCookie","holdConsent","revokeConsent","grantConsent"],ttq.setAndDefer=function(t,e){t[e]=function(){t.push([e].concat(Array.prototype.slice.call(arguments,0)))}};for(var i=0;i<ttq.methods.length;i++)ttq.setAndDefer(ttq,ttq.methods[i]);ttq.instance=function(t){for(var e=ttq._i[t]||[],n=0;n<ttq.methods.length;n++)ttq.setAndDefer(e,ttq.methods[n]);return e},ttq.load=function(e,n){var r="https://analytics.tiktok.com/i18n/pixel/events.js",o=n&&n.partner;ttq._i=ttq._i||{},ttq._i[e]=[],ttq._i[e]._u=r,ttq._t=ttq._t||{},ttq._t[e]=+new Date,ttq._o=ttq._o||{},ttq._o[e]=n||{};n=d.createElement("script");n.type="text/javascript",n.async=!0,n.src=r+"?sdkid="+e+"&lib="+t;e=d.getElementsByTagName("script")[0];e.parentNode.insertBefore(n,e)}}(window,document,"ttq")

      window.ttq!.load(this.trackingId)
      this.identifyUser()
      window.ttq!.page()
    }
    /* eslint-enable */

    const trackerAsPlugin: ExternalTrackerAsPlugin = {
      trackingHandler,
      loadOnCustomEvent: true,
    }

    this.initializeTrackerAsPlugin(trackerAsPlugin)
  }
}
